import { useStores } from "@sushicorp/contexts";
import { useCatalogues } from "@sushicorp/contexts";
import { useFetchStores } from "@sushicorp/services";
import { isTouchScreenDevice } from "@sushicorp/utils";
import { debounce } from "@sushicorp/utils";
import { getMaxWidth } from "@sushicorp/utils";
import { Store } from "artisn/types";
import { SearchBar, StaticMap } from "artisn-ui-react";
import React, { useState } from "react";

import Styles, { ModalStyled } from "./ProfileStores.styles";
import { ProfileStoresProps as Props } from "./ProfileStores.types";
import StoreList from "components/chooseStore/StoreList/StoreList";
import Footer from "components/global/Footer/Footer";
import GoBack from "components/global/GoBack/GoBack";
import Navbar from "components/global/Navbar/Navbar";
import ProfileNavigation from "components/global/ProfileNavigation/ProfileNavigation";
import { formatScheduleOutputHelper } from "components/global/Schedule/Schedule.helpers";
import { getENVs } from "config/artisn.config";
import CONSTANTS from "config/constants";
import useI18n from "hooks/useI18n";
import useWindowSize from "hooks/useWindowSize";
import { notify } from "utils/common.utils";

import CloseSVG from "../../../../public/assets/images/close.svg";
import AddressSVG from "../../../../public/assets/images/profile/stores/address.svg";
import PhoneSVG from "../../../../public/assets/images/profile/stores/phone.svg";
import TimeSVG from "../../../../public/assets/images/profile/stores/time.svg";

const { BREAKPOINTS, FEATURE_FLAGS } = CONSTANTS;
const { mobile } = BREAKPOINTS;
const { WITH_BOTTOM_SEARCH, WITH_BOTTOM_RESPONSIVE_BUTTONS } = FEATURE_FLAGS;

const ProfileStores: React.FC<Props> = props => {
  const t = useI18n();
  const { data: stores, isLoading, isError } = useFetchStores(notify);
  const { setSelectedStore, selectedStore } = useStores();
  const { selectedCatalogueId } = useCatalogues();
  const isTouchDevice = typeof isTouchScreenDevice() === "boolean";
  const maxWidth = +getMaxWidth().replace("px", "");
  const { width } = useWindowSize();
  const [mapStore, setMapStore] = useState<Store | undefined>(selectedStore);
  const [opened, setOpened] = useState(false);
  const [query, setQuery] = useState<string | undefined>();
  const { schedules, phone, storeName, isOpen } = selectedStore ?? {};
  const { address } = selectedStore ?? {};
  const { latitude: lat, longitude: lng } = mapStore ?? {};
  const isMobile = width < mobile;

  const submitHandler = (query: string) => {
    setQuery(query);
  };

  const onSelectedStore = (store: Store) => {
    setSelectedStore(store);
    setMapStore(store);
    if (isTouchDevice) {
      setOpened(true);
    }
  };

  const queryChangeHandler = debounce((value: string) => {
    if (!value.length) setQuery("");
    if (value.length < 3) return;
    setQuery(value);
  }, 500);

  const clearHandler = () => {
    setQuery("");
  };

  const storeInfoNode = (
    <div className="ProfileStores__info">
      <div className="ProfileStores__map">
        <h2 className="ProfileStores__title">
          {isMobile ? t.profile.stores.title : storeName}
        </h2>
        {selectedStore ? (
          <StaticMap
            googleMapsKey={`${getENVs?.mapsApiKey}`}
            width={isTouchDevice ? maxWidth - 32 : 640}
            height={240}
            className="ProfileStores__static-map"
            markers={[
              {
                coordinates: {
                  lat: lat!,
                  lng: lng!
                },
                icon: "https://res.cloudinary.com/cbazcloud/image/upload/v1662393990/Group_185_sjtbul.png"
              }
            ]}
          />
        ) : null}
      </div>
      <div className="ProfileStores__opened">
        {isOpen ? (
          <div className="ProfileStores__point ProfileStores__point--active" />
        ) : (
          <div className="ProfileStores__point" />
        )}
        {isOpen ? t.profile.stores.open : t.profile.stores.closed}
      </div>
      <div className="ProfileStores__location">
        <AddressSVG />
        {t.profile.stores.address}:
        <p className="ProfileStores__text" title={address}>
          {address}
        </p>
      </div>
      <div className="ProfileStores__phone">
        <PhoneSVG />
        {t.profile.stores.phone}:
        <p className="ProfileStores__text" title={phone}>
          {phone}
        </p>
      </div>
      <div className="ProfileStores__time">
        <TimeSVG />
        <div>
          <p
            className="ProfileStores__text ProfileStores__section-title"
            title={"Horarios"}
          >
            {t.profile.stores.schedules}:
          </p>
          {schedules
            ?.filter(schedule => schedule.catalogueId === selectedCatalogueId)
            .filter(
              schedule =>
                schedule.day === "MONDAY" ||
                schedule.day === "SATURDAY" ||
                schedule.day === "SUNDAY"
            )
            .map((schedule, index) => (
              <p
                className="ProfileStores__text"
                key={index}
                title={formatScheduleOutputHelper(
                  schedule,
                  t.common.from,
                  t.common.to
                )}
              >
                {formatScheduleOutputHelper(
                  schedule,
                  t.common.from,
                  t.common.to
                )}
              </p>
            ))}
        </div>
      </div>
    </div>
  );

  return (
    <Styles
      className="ProfileStores"
      withBottomSearch={WITH_BOTTOM_SEARCH}
      withBottomResponsiveButtons={WITH_BOTTOM_RESPONSIVE_BUTTONS}
    >
      <header className="ProfileStores__header">
        <Navbar showBrand />
      </header>
      <main className="ProfileStores__main">
        <ProfileNavigation className="ProfileStores__profile-navigation" />
        <div className="ProfileStores__content">
          <GoBack className="ProfileStores__go-back" title={t.common.goBack} />
          <div className="ProfileStores__wrapper">
            <div className="ProfileStores__map ProfileStores__map--mobile">
              <h2 className="ProfileStores__title">{t.profile.stores.title}</h2>
              {selectedStore ? (
                <StaticMap
                  googleMapsKey={`${getENVs?.mapsApiKey}`}
                  width={isTouchDevice ? maxWidth - 32 : 640}
                  height={240}
                  className="ProfileStores__static-map"
                  markers={[
                    {
                      coordinates: {
                        lat: lat!,
                        lng: lng!
                      },
                      icon: "https://res.cloudinary.com/cbazcloud/image/upload/v1662393990/Group_185_sjtbul.png"
                    }
                  ]}
                />
              ) : null}
            </div>
            <div className="ProfileStores__bottom">
              <div className="ProfileStores__list">
                <SearchBar
                  className="ProfileStores__search-bar field"
                  onSubmit={submitHandler}
                  placeholder={t.common.search}
                  onChange={queryChangeHandler}
                  onClear={clearHandler}
                />
                <StoreList
                  className="ProfileStores__stores"
                  stores={stores}
                  isLoading={isLoading}
                  isError={isError}
                  selectedStore={mapStore}
                  onStoreClick={onSelectedStore}
                  query={query}
                />
              </div>
              {!isTouchDevice && selectedStore ? storeInfoNode : null}
            </div>
          </div>
        </div>
      </main>
      <ModalStyled
        className="ModalInfoLocal"
        opened={isTouchDevice && opened}
        closeIcon={<CloseSVG />}
        onClose={() => setOpened(false)}
      >
        {selectedStore ? storeInfoNode : null}
      </ModalStyled>
      <footer className="ProfileStores__footer">
        <Footer />
      </footer>
    </Styles>
  );
};

ProfileStores.defaultProps = {};

export default ProfileStores;
